import { addDaysUtc } from "@acto/web-common/dates";
import type { CalendarCell } from "./types";

export {
  utcDate,
  todayUtc,
  startOfDayUtc,
  startOfMonthUtc,
  addDaysUtc,
  addMonthsUtc,
  startOfWeekUtc,
  toIsoDate,
  isoToUtc,
  isoWeekNumber,
  quarterOfUtc,
} from "@acto/web-common/dates";

// The formatters pin timeZone UTC to match the UTC-midnight date math. The locale is supplied per call.

export interface CalendarFormat {
  /** The seven Mo–So labels for the week starting at `mondayStart`. */
  weekdayLabels(mondayStart: Date): string[];
  toCell(date: Date): CalendarCell;
  monthTitle(date: Date): string;
  weekRange(start: Date, end: Date): string;
}

/** Builds the locale-bound formatters the calendar needs (one Intl set per locale). */
export function createCalendarFormat(locale: string): CalendarFormat {
  const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" });
  const fullDate = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  const monthYear = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" });
  const dayOnly = new Intl.DateTimeFormat(locale, { day: "numeric", timeZone: "UTC" });
  const dayMonth = new Intl.DateTimeFormat(locale, { day: "numeric", month: "long", timeZone: "UTC" });

  return {
    weekdayLabels: (mondayStart) =>
      Array.from({ length: 7 }, (_, day) => weekdayShort.format(addDaysUtc(mondayStart, day)).replace(/\.$/, "")),
    toCell: (date) => ({ date, label: String(date.getUTCDate()), ariaLabel: fullDate.format(date) }),
    monthTitle: (date) => monthYear.format(date),
    weekRange: (start, end) => {
      const sameMonth = start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth();

      return sameMonth
        ? `${dayOnly.format(start)} - ${dayMonth.format(end)}`
        : `${dayMonth.format(start)} - ${dayMonth.format(end)}`;
    },
  };
}
